/**
 * Tracks "page-load related" requests (fetch + manually-wrapped async work).
 * Lets callers wait until requests have settled after window.load using a quiet-window heuristic.
 *
 * Migrated from Solid CMS — axios interceptors removed since the Next.js app uses fetch.
 */

type SettledResolveFn = (value: boolean) => void;

type TrackedRequestMeta = {
	kind: "fetch" | "manual";
	name: string;
	url?: string;
};

export type PageLoadRequestsSettledResult = {
	settled: boolean;
	isTrackingWindowClosed: boolean;
	inFlightCount: number;
	trackedRequestCount: number;
	apiBatchDurationMs: number;
	apiBatchStartPerfMs: number;
	apiBatchEndPerfMs: number;
};

let isTrackingInitialized = false;
let hasWindowLoadFired = false;
let isPostLoadTrackingWindowClosed = false;
let inFlightCount = 0;

let settledResolve: SettledResolveFn | null = null;
let settledPromise: Promise<boolean> | null = null;

let isFetchWrapped = false;
let originalWindowFetch: typeof fetch | null = null;

const metaByToken = new Map<string, TrackedRequestMeta>();
const startPerfByToken = new Map<string, number>();

const POST_LOAD_QUIET_PERIOD_MS = 800;
const MAX_WAIT_AFTER_WINDOW_LOAD_MS = 60000;

let lastTrackedStartWallClockMs = 0;
let apiBatchStartPerfMs: number | null = null;
let apiBatchEndPerfMs: number | null = null;
let trackedRequestCount = 0;
let quietPeriodTimerId: ReturnType<typeof setTimeout> | null = null;
let maxWaitAfterLoadTimerId: ReturnType<typeof setTimeout> | null = null;

let cachedApiBatchDurationMs = 0;

let tokenSequence = 0;

const nextTrackingToken = (): string => {
	tokenSequence = (tokenSequence + 1) % Number.MAX_SAFE_INTEGER;
	return `${Date.now().toString(36)}-${tokenSequence.toString(36)}`;
};

const getApiBatchStartPerfMs = (): number => {
	return apiBatchStartPerfMs ?? 0;
};

const getApiBatchEndPerfMs = (): number => {
	if (apiBatchStartPerfMs === null) return 0;
	if (isPostLoadTrackingWindowClosed) {
		return apiBatchEndPerfMs ?? apiBatchStartPerfMs + cachedApiBatchDurationMs;
	}
	return apiBatchEndPerfMs ?? performance.now();
};

const maybeResolveSettled = () => {
	if (!settledResolve) return;
	if (hasWindowLoadFired && isPostLoadTrackingWindowClosed && inFlightCount === 0) {
		const resolve = settledResolve;
		settledResolve = null;
		settledPromise = null;
		resolve(true);
	}
};

const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
	if (timer === null) return;
	globalThis.clearTimeout(timer);
};

const schedulePostLoadQuietPeriodCheck = () => {
	if (globalThis.window === undefined) return;
	if (!hasWindowLoadFired || isPostLoadTrackingWindowClosed) return;

	clearTimer(quietPeriodTimerId);
	quietPeriodTimerId = globalThis.setTimeout(() => {
		quietPeriodTimerId = null;
		if (isPostLoadTrackingWindowClosed) return;
		if (inFlightCount !== 0) return;
		if (Date.now() - lastTrackedStartWallClockMs < POST_LOAD_QUIET_PERIOD_MS) return;

		isPostLoadTrackingWindowClosed = true;
		const durationMs =
			apiBatchStartPerfMs !== null && apiBatchEndPerfMs !== null
				? Math.max(0, apiBatchEndPerfMs - apiBatchStartPerfMs)
				: 0;
		cachedApiBatchDurationMs = durationMs;
		maybeResolveSettled();
	}, POST_LOAD_QUIET_PERIOD_MS);
};

const tryGetResourceTimingResponseEndPerfMs = (
	url: string,
	kind: TrackedRequestMeta["kind"],
	startedAtPerfMs: number,
): number | null => {
	if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
		return null;
	}
	const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
	if (!entries || entries.length === 0) return null;

	let expectedInitiator: string | null = null;
	if (kind === "fetch") {
		expectedInitiator = "fetch";
	}
	const startWindowMs = Math.max(0, startedAtPerfMs - 200);

	for (let i = entries.length - 1; i >= 0; i -= 1) {
		const e = entries[i];
		if (!e?.name || e.name !== url) continue;
		if (expectedInitiator && e.initiatorType !== expectedInitiator) continue;
		if (e.startTime < startWindowMs) continue;
		if (!Number.isFinite(e.responseEnd) || e.responseEnd <= 0) continue;
		return e.responseEnd;
	}

	return null;
};

const beginTrackedRequest = (meta?: TrackedRequestMeta): string | null => {
	if (!isTrackingInitialized || isPostLoadTrackingWindowClosed) return null;
	inFlightCount += 1;
	lastTrackedStartWallClockMs = Date.now();
	trackedRequestCount += 1;
	apiBatchStartPerfMs ??= performance.now();
	const token = nextTrackingToken();
	startPerfByToken.set(token, performance.now());
	if (meta) {
		metaByToken.set(token, meta);
	}
	schedulePostLoadQuietPeriodCheck();
	return token;
};

const finishTrackedRequest = (token: string | null | undefined) => {
	if (!token) return;
	const meta = metaByToken.get(token);
	const startedAtPerfMs = startPerfByToken.get(token);
	const resourceEndPerfMs =
		meta?.url && startedAtPerfMs !== undefined
			? tryGetResourceTimingResponseEndPerfMs(meta.url, meta.kind, startedAtPerfMs)
			: null;
	const endPerfMs = resourceEndPerfMs ?? performance.now();
	apiBatchEndPerfMs = endPerfMs;
	metaByToken.delete(token);
	startPerfByToken.delete(token);
	if (inFlightCount > 0) inFlightCount -= 1;
	maybeResolveSettled();
	if (hasWindowLoadFired && !isPostLoadTrackingWindowClosed && inFlightCount === 0) {
		schedulePostLoadQuietPeriodCheck();
	}
};

const setupLoadListenerAndTimers = (): void => {
	lastTrackedStartWallClockMs = Date.now();
	clearTimer(maxWaitAfterLoadTimerId);
	maxWaitAfterLoadTimerId = globalThis.setTimeout(() => {
		maxWaitAfterLoadTimerId = null;
		if (isPostLoadTrackingWindowClosed) return;
		isPostLoadTrackingWindowClosed = true;
		const durationMs = apiBatchStartPerfMs === null ? 0 : Math.max(0, performance.now() - apiBatchStartPerfMs);
		cachedApiBatchDurationMs = durationMs;
		maybeResolveSettled();
	}, MAX_WAIT_AFTER_WINDOW_LOAD_MS);
	schedulePostLoadQuietPeriodCheck();
};

export const initPageLoadRequestTracking = (): void => {
	if (globalThis.window === undefined) return;
	if (isTrackingInitialized) return;
	isTrackingInitialized = true;

	hasWindowLoadFired = document.readyState === "complete";
	if (hasWindowLoadFired) {
		setupLoadListenerAndTimers();
	} else {
		globalThis.window.addEventListener(
			"load",
			() => {
				hasWindowLoadFired = true;
				setupLoadListenerAndTimers();
			},
			{ once: true },
		);
	}

	// Wrap native fetch
	if (!isFetchWrapped && typeof globalThis.window.fetch === "function") {
		isFetchWrapped = true;
		const globalFlag = "__bflInitialLoadFetchWrapped";
		if ((globalThis as unknown as Record<string, unknown>)[globalFlag]) {
			return;
		}
		(globalThis as unknown as Record<string, unknown>)[globalFlag] = true;

		originalWindowFetch = globalThis.window.fetch.bind(globalThis.window);
		globalThis.window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
			let url: string;
			if (typeof input === "string") {
				url = input;
			} else if (input instanceof URL) {
				url = input.toString();
			} else {
				url = input.url;
			}

			const isRequestObject = typeof input !== "string" && !(input instanceof URL);
			const rawMethod = init?.method ?? (isRequestObject ? input.method : "GET");
			const method = (rawMethod ?? "GET").toString().toUpperCase();
			const token = beginTrackedRequest({ kind: "fetch", name: `${method} ${url}`, url });
			try {
				const response = await (originalWindowFetch as typeof fetch)(input as RequestInfo, init);
				finishTrackedRequest(token);
				return response;
			} catch (e) {
				finishTrackedRequest(token);
				throw e;
			}
		}) as typeof fetch;
	}
};

export const trackPageLoadAsyncWork = async <T>(work: () => Promise<T>, meta?: TrackedRequestMeta): Promise<T> => {
	initPageLoadRequestTracking();
	const token = beginTrackedRequest(meta ?? { kind: "manual", name: "work" });
	try {
		return await work();
	} finally {
		finishTrackedRequest(token);
	}
};

const computeApiBatchDurationMs = (): number => {
	if (apiBatchStartPerfMs === null) return 0;
	if (isPostLoadTrackingWindowClosed) {
		return cachedApiBatchDurationMs;
	}
	if (apiBatchEndPerfMs !== null) {
		return Math.max(0, apiBatchEndPerfMs - apiBatchStartPerfMs);
	}
	return Math.max(0, performance.now() - apiBatchStartPerfMs);
};

export const waitForPageLoadRequestsToSettle = (timeoutMs = 60000): Promise<PageLoadRequestsSettledResult> => {
	initPageLoadRequestTracking();

	if (hasWindowLoadFired && isPostLoadTrackingWindowClosed && inFlightCount === 0) {
		return Promise.resolve({
			settled: true,
			isTrackingWindowClosed: isPostLoadTrackingWindowClosed,
			inFlightCount,
			trackedRequestCount,
			apiBatchDurationMs: computeApiBatchDurationMs(),
			apiBatchStartPerfMs: getApiBatchStartPerfMs(),
			apiBatchEndPerfMs: getApiBatchEndPerfMs(),
		});
	}

	settledPromise ??= new Promise<boolean>((resolve) => {
		settledResolve = resolve;
		maybeResolveSettled();
	});

	return Promise.race([
		settledPromise,
		new Promise<boolean>((resolve) => {
			globalThis.setTimeout(() => resolve(false), timeoutMs);
		}),
	]).then((settled) => ({
		settled,
		isTrackingWindowClosed: isPostLoadTrackingWindowClosed,
		inFlightCount,
		trackedRequestCount,
		apiBatchDurationMs: computeApiBatchDurationMs(),
		apiBatchStartPerfMs: getApiBatchStartPerfMs(),
		apiBatchEndPerfMs: getApiBatchEndPerfMs(),
	}));
};
